import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useGuestGate } from "../lib/useGuestGate";
import { useProperty } from "../lib/PropertyContext";
import PropertySwitcher from "../components/customer/PropertySwitcher";
import ICalSyncPanel from "../components/ICalSyncPanel";
import BookingDetailModal from "../components/BookingDetailModal";
import { BRAND_COLORS, bookingPill, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";
import { useOnline } from "../lib/offline";

export default function CustomerBooking() {
  const { t, lang } = useI18n();
  const gate = useGuestGate();
  const online = useOnline();
  // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter global: `properties` + `selected`
  // stammen jetzt aus dem PropertyContext, damit Wechsel im Dashboard hier
  // sofort sichtbar sind (und umgekehrt).
  const { properties, selected } = useProperty();
  const [extras, setExtras] = useState([]);
  const [standardServices, setStandardServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ property_id: "", from_date: "", to_date: "", services: [], notes: "" });
  const [loading, setLoading] = useState(false);
  const [showStandard, setShowStandard] = useState(false);
  // Feb 2026 · 16 Sep 2026 · User-Directive „Kunde ändert An-/Abreisedatum
  // seiner eigenen Buchung selbst — Save mit Customer-PIN":
  // Jede Zeile in „Kommende Termine" ist jetzt klickbar und öffnet das
  // BookingDetailModal. Der Kunde kann dort auf „Termin ändern" gehen,
  // Datum verschieben und mit persönlicher PIN bestätigen.
  const [openBookingId, setOpenBookingId] = useState(null);

  const load = async () => {
    const [e, s, b] = await Promise.all([
      api.get("/extras"),
      api.get("/standard-services"),
      api.get("/bookings"),
    ]);
    setExtras(e.data);
    setStandardServices(s.data);
    setBookings(b.data);
  };

  useEffect(() => { load(); }, []);
  // Feb 2026 · 8 Sep 2026 · Form immer auf das global aktive Objekt binden.
  useEffect(() => {
    if (selected?.id) setForm((f) => ({ ...f, property_id: selected.id }));
  }, [selected?.id]);

  const toggleExtra = (id) => {
    setForm((f) => ({ ...f, services: f.services.includes(id) ? f.services.filter((x) => x !== id) : [...f.services, id] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (gate.isGuest) { gate.promptUpgrade(); return; }
    if (!form.property_id || !form.from_date || !form.to_date) return toast.error(lang === "en" ? "Please pick a period and property." : "Bitte Zeitraum und Objekt wählen.");
    setLoading(true);
    try {
      await api.post("/bookings", form);
      toast.success(t.booking.created);
      setForm({ ...form, services: [], notes: "" });
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Feb 2026 · Sortier-Directive: Aktive/offene Termine zuerst
  // (chronologisch aufsteigend), stornierte Termine IMMER am Ende, egal
  // wie nah das Datum ist.
  // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter global: Filter nach aktivem
  // Objekt aus dem Context.
  const upcoming = bookings
    .filter((b) => (selected?.id ? b.property_id === selected.id : true))
    .filter((b) => b.to_date >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => {
      const aCancelled = a.status === "cancelled" ? 1 : 0;
      const bCancelled = b.status === "cancelled" ? 1 : 0;
      if (aCancelled !== bCancelled) return aCancelled - bCancelled;
      return (a.from_date || "").localeCompare(b.from_date || "");
    });

  const categoryLabels = t.catalogue.categories;
  const extrasByCategory = extras.reduce((acc, ex) => {
    (acc[ex.category] = acc[ex.category] || []).push(ex);
    return acc;
  }, {});

  // Recently booked extras from history — dedupe by extra id, most recent bookings first.
  const recentExtraIds = (() => {
    const seen = new Set();
    const ordered = [];
    const sortedBookings = [...bookings].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    for (const b of sortedBookings) {
      for (const eid of (b.services || [])) {
        if (!seen.has(eid)) {
          seen.add(eid);
          ordered.push(eid);
        }
      }
    }
    return ordered.slice(0, 10);
  })();
  const extraById = Object.fromEntries(extras.map((e) => [e.id, e]));
  const recentExtras = recentExtraIds.map((id) => extraById[id]).filter(Boolean);

  const addExtra = (id) => {
    setForm((f) => (f.services.includes(id) ? f : { ...f, services: [...f.services, id] }));
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16" data-testid="customer-booking">
      <div className="mb-12 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="label-caps mb-3">Concierge</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">{t.booking.title}</h1>
          <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">{t.booking.lead}</p>
        </div>
        <button
          type="button"
          data-testid="view-standard-services-btn"
          onClick={() => setShowStandard(true)}
          className="border border-navy text-navy px-5 py-2.5 text-[12px] tracking-[0.15em] uppercase hover:bg-navy hover:text-ivory transition-colors"
        >
          {t.booking.view_standard}
        </button>
      </div>

      {/* Feb 2026 · 8 Sep 2026 · Globaler Objekt-Umschalter — rendert nichts
          bei Ein-Objekt-Kunden. Wechsel spiegelt sich sofort im Formular
          `property_id` (via `useEffect` oben) UND in der Liste `upcoming`. */}
      <PropertySwitcher testidPrefix="booking-property-switcher" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-navy/10 p-8 space-y-8" data-testid="booking-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-caps block mb-2">{t.booking.from}</label>
              <input
                data-testid="booking-from-input"
                type="date" required
                value={form.from_date}
                onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
              />
            </div>
            <div>
              <label className="label-caps block mb-2">{t.booking.to}</label>
              <input
                data-testid="booking-to-input"
                type="date" required
                value={form.to_date}
                onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
              />
            </div>
          </div>

          {properties.length > 0 && (
            <div>
              <label className="label-caps block mb-2">{t.booking.property}</label>
              <select
                data-testid="booking-property-select"
                value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
              >
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="label-caps">{t.booking.extras_label}</label>
              <span className="text-[11px] text-navy-dark/60 italic">{t.booking.extras_hint}</span>
            </div>

            {recentExtras.length > 0 && (
              <div className="mb-6 p-4 bg-ivory/50 border border-gold/30" data-testid="recent-extras">
                <div className="flex items-baseline justify-between mb-3">
                  <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>{t.booking.recent_title}</div>
                  <span className="text-[11px] text-navy-dark/60 italic">{t.booking.recent_lead}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentExtras.map((ex) => {
                    const active = form.services.includes(ex.id);
                    return (
                      <button
                        key={ex.id}
                        type="button"
                        data-testid={`recent-extra-${ex.key}`}
                        onClick={() => addExtra(ex.id)}
                        className={`text-left px-3 py-1.5 text-[12px] border transition-colors ${active ? "border-gold bg-gold text-navy" : "border-navy/25 bg-white text-navy hover:border-gold"}`}
                      >
                        {active ? "✓ " : "+ "}{lang === "en" ? ex.name_en : ex.name_de}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-6">
              {Object.entries(extrasByCategory).map(([cat, list]) => (
                <div key={cat}>
                  <div className="font-display text-lg text-navy mb-3 flex items-baseline gap-3">
                    <span>{categoryLabels[cat] || cat}</span>
                    <span className="flex-1 border-b border-gold/40" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {list.map((ex) => {
                      const active = form.services.includes(ex.id);
                      return (
                        <button
                          key={ex.id}
                          type="button"
                          data-testid={`booking-extra-${ex.key}`}
                          onClick={() => toggleExtra(ex.id)}
                          className={`text-left border p-3 transition-colors ${active ? "border-gold bg-gold/5" : "border-navy/15 bg-white hover:border-navy/40"}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-[13px] text-navy leading-snug">{lang === "en" ? ex.name_en : ex.name_de}</div>
                            <div className={`shrink-0 w-4 h-4 border ${active ? "bg-gold border-gold" : "border-navy/30"}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="label-caps block mb-2">{t.booking.notes}</label>
            <textarea
              data-testid="booking-notes-input"
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark leading-relaxed"
              placeholder={lang === "en" ? "Special wishes…" : "Besondere Wünsche…"}
            />
          </div>

          <button
            data-testid="booking-submit-btn"
            data-guest-locked={gate.isGuest ? "1" : "0"}
            type="submit"
            disabled={loading || !online}
            className="inline-flex items-center gap-2 bg-navy text-ivory px-10 py-3.5 text-[13px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-50"
            style={{ background: BRAND_COLORS.navy }}
          >
            {gate.isGuest && <Lock className="w-3.5 h-3.5" strokeWidth={1.6} />}
            {loading ? t.common.loading : t.booking.submit}
          </button>
        </form>

        {gate.dialog}

        <aside className="space-y-6 h-fit">
          <div className="bg-white border border-navy/10 p-6">
            <div className="label-caps mb-4">{t.booking.upcoming}</div>
            {upcoming.length === 0 ? (
              <div className="text-[13px] text-navy-dark/50 py-8 text-center border border-dashed border-navy/15">{t.booking.no_upcoming}</div>
            ) : (
              <ul className="space-y-4" data-testid="upcoming-bookings-list">
                {upcoming.map((b) => {
                  // Feb 2026 · Systemweiter 4-Farben-Statusstreifen am
                  // linken Rand (Grau / Blau / Grün / Rot diagonal),
                  // deckungsgleich mit Admin-Buchungsliste und Kalender.
                  const pill = bookingPill(b);
                  const cancelled = b.status === "cancelled";
                  const stripeBg = cancelled
                    ? `repeating-linear-gradient(135deg, ${STATUS_COLORS.red} 0 4px, ${withAlpha(STATUS_COLORS.red, 0.35)} 4px 8px)`
                    : pill.bg;
                  return (
                  <li
                    key={b.id}
                    className="relative pl-4 pb-4 border-b border-navy/10 last:border-b-0"
                    data-testid={`upcoming-booking-item-${b.id}`}
                    data-status={b.status || "requested"}
                  >
                    <span
                      aria-hidden="true"
                      data-testid={`upcoming-booking-stripe-${b.id}`}
                      className="absolute left-0 top-0 bottom-4 w-1"
                      style={{ background: stripeBg }}
                    />
                    <button
                      type="button"
                      onClick={() => setOpenBookingId(b.id)}
                      data-testid={`upcoming-booking-open-${b.id}`}
                      className="w-full text-left group"
                      title={lang === "en" ? "Open booking · edit dates" : "Termin öffnen · Datum ändern"}
                    >
                      <div className="font-display text-xl text-navy group-hover:text-gold transition-colors">{deDateMedium(b.from_date)}</div>
                      <div className="text-[12px] text-navy-dark/60 mt-1">{lang === "en" ? "until " : "bis "}{deDateMedium(b.to_date)}</div>
                      <div className="mt-2 label-caps">{t.requests.status[b.status] || b.status}</div>
                      {b.notes && <div className="text-[12px] text-navy-dark/70 mt-2 leading-relaxed italic">"{b.notes}"</div>}
                      <div className="text-[10.5px] tracking-[0.15em] uppercase text-navy-dark/40 mt-2 group-hover:text-gold transition-colors">
                        {lang === "en" ? "Tap to edit dates →" : "Termin ändern →"}
                      </div>
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
          <ICalSyncPanel />
        </aside>
      </div>

      {/* Feb 2026 · 16 Sep 2026 · Buchungs-Detail-Modal.
          Öffnet sich wenn der Kunde eine Zeile in „Kommende Termine"
          antippt. Trägt den kompletten Edit-Flow inkl. Datumsänderung,
          Customer-PIN-Bestätigung und Overlap-Warnung. */}
      {openBookingId && (
        <BookingDetailModal
          booking={bookings.find((b) => b.id === openBookingId)}
          extras={extras}
          propertyName={properties.find((p) => p.id === bookings.find((b) => b.id === openBookingId)?.property_id)?.name}
          onClose={() => setOpenBookingId(null)}
          onChanged={async () => {
            try {
              const { data } = await api.get("/bookings");
              setBookings(data);
            } catch { /* silent */ }
          }}
        />
      )}

      {/* Standard services dialog */}
      {showStandard && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.75)}` }}
          data-testid="standard-services-dialog"
          onClick={() => setShowStandard(false)}
        >
          <div
            className="bg-ivory max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-gold/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 border-b border-navy/10 sticky top-0 bg-ivory z-10 flex items-start justify-between gap-4">
              <div>
                <div className="label-caps mb-2">Weissblom</div>
                <h2 className="font-display text-3xl text-navy">{t.catalogue.standard_title}</h2>
                <p className="text-[13px] text-navy-dark/70 mt-2">{t.catalogue.standard_lead}</p>
              </div>
              <button
                data-testid="close-standard-dialog"
                onClick={() => setShowStandard(false)}
                className="label-caps text-navy-dark/70 hover:text-navy transition-colors"
              >
                {t.common.close}
              </button>
            </div>
            <ul className="p-8 space-y-3">
              {standardServices.map((s, i) => (
                <li key={s.id} className="flex items-start gap-4 pb-3 border-b border-navy/10 last:border-b-0">
                  <span className="font-display text-gold text-lg leading-none pt-1 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[14px] text-navy-dark leading-relaxed">{lang === "en" ? s.name_en : s.name_de}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
